import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Download,
  Loader2,
  BadgeCheck,
  AlertTriangle,
  ScrollText,
  Mail,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { getPresignedDownload, deleteDocument } from '@/services/documentsService';
import { listarCertificados } from './Services/documentos-personal.services';
import CertificadoLaboralModal from './CertificadoLaboralModal';

// Catálogo de documentos del módulo. Se va creciendo con los próximos tipos;
// por eso está declarado como datos y no como JSX suelto.
const TIPOS_DOCUMENTO = [
  {
    id: 'certificado_laboral',
    nombre: 'Certificado laboral',
    descripcion:
      'Acredita el cargo, la fecha de ingreso y, si se pide, la remuneración. Se firma y queda en el expediente del empleado.',
    icon: BadgeCheck,
    disponible: true,
  },
  {
    id: 'llamado_atencion',
    nombre: 'Llamado de atención',
    descripcion:
      'Comunicación formal con versión de cargos y descargos, previo a cualquier sanción.',
    icon: AlertTriangle,
    disponible: false,
  },
  {
    id: 'sancion',
    nombre: 'Sanción disciplinaria',
    descripcion: 'Resolución de la sanción después del proceso de descargos.',
    icon: ScrollText,
    disponible: false,
  },
  {
    id: 'carta_recomendacion',
    nombre: 'Carta de recomendación',
    descripcion: 'Certificación laboral de buena conducta y desempeño.',
    icon: Mail,
    disponible: false,
  },
];

// Modal de generación por tipo de documento. Se separa del catálogo para que
// la página no tenga que saber qué campos pide cada documento: para agregar un
// tipo basta con su entrada en TIPOS_DOCUMENTO y su modal en este mapa.
const MODALES = {
  certificado_laboral: CertificadoLaboralModal,
};

const DocumentosPage = () => {
  const { toast } = useToast();
  // El selector de tipo y el modal de generación son dos estados distintos: el
  // primero elige qué emitir, el segundo pide los datos. Se encadenan sin
  // abrir los dos a la vez, porque dos diálogos apilados en Radix no se
  // comportan bien.
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [tipoActivo, setTipoActivo] = useState(null);
  const [certificados, setCertificados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [descargando, setDescargando] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const ModalTipo = tipoActivo ? MODALES[tipoActivo] : null;

  const elegirTipo = (tipo) => {
    if (!tipo.disponible || !MODALES[tipo.id]) return;
    setSelectorOpen(false);
    setTipoActivo(tipo.id);
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listarCertificados();
      setCertificados(Array.isArray(res) ? res : []);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const descargar = async (id) => {
    setDescargando(id);
    try {
      const url = await getPresignedDownload({ id });
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setDescargando(null);
    }
  };

  const confirmarEliminar = async () => {
    setEliminando(true);
    try {
      // El endpoint borra el PDF de R2 y el registro en Mongo; no queda copia.
      await deleteDocument({ id: aEliminar.id });
      toast({ title: 'Documento eliminado', description: 'Ya no está en el expediente.' });
      setAEliminar(null);
      cargar();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setEliminando(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Certificados y comunicaciones formales del personal. Cada documento queda guardado en
            el expediente de quien lo recibe.
          </p>
          <Button onClick={() => setSelectorOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Generar documento
          </Button>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-gray-700">Certificados emitidos</h2>
          </div>

          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Documento</TableHead>
                  <TableHead>Archivo</TableHead>
                  <TableHead>Generado</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      Cargando...
                    </TableCell>
                  </TableRow>
                ) : certificados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      Todavía no se ha emitido ningún certificado.
                    </TableCell>
                  </TableRow>
                ) : (
                  certificados.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">{doc.nombre}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{doc.fileName}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {doc.creadoEn
                          ? new Date(doc.creadoEn).toLocaleDateString('es-CO', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => descargar(doc.id)}
                            disabled={descargando === doc.id}
                          >
                            {descargando === doc.id ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="mr-2 h-4 w-4" />
                            )}
                            Descargar
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Eliminar documento"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => setAEliminar(doc)}
                            disabled={eliminando && aEliminar?.id === doc.id}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <AlertDialog
        open={!!aEliminar}
        onOpenChange={(o) => {
          if (!o && !eliminando) setAEliminar(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borrará <strong>{aEliminar?.fileName}</strong> del expediente del empleado. Esta
              acción no se puede deshacer: el PDF se elimina del almacenamiento y ya no podrá
              descargarse. Si solo te equivocaste en el contenido, genera uno nuevo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={eliminando}
              onClick={(e) => {
                // Se evita el cierre automático para que la fila siga visible
                // mientras corre el borrado; si falla, se conserva el diálogo
                // con el mensaje de error.
                e.preventDefault();
                confirmarEliminar();
              }}
            >
              {eliminando ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={selectorOpen} onOpenChange={setSelectorOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>¿Qué documento vas a generar?</DialogTitle>
            <DialogDescription>
              Escoge el tipo de documento y luego completa los datos que pide.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {TIPOS_DOCUMENTO.map((tipo) => {
              const Icon = tipo.icon;
              // Se puede seleccionar solo lo que tiene modal montado, aunque
              // esté marcado como disponible: evita dejar entradas del catálogo
              // que no hacen nada al pulsarlas.
              const habilitado = tipo.disponible && !!MODALES[tipo.id];

              return (
                <button
                  key={tipo.id}
                  type="button"
                  disabled={!habilitado}
                  onClick={() => elegirTipo(tipo)}
                  className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                    habilitado
                      ? 'hover:bg-accent hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-ring'
                      : 'opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className="p-2 rounded-md bg-primary/15 shrink-0">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{tipo.nombre}</p>
                      {!habilitado && (
                        <Badge variant="outline">
                          {tipo.disponible ? 'No disponible' : 'Pronto'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{tipo.descripcion}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {ModalTipo && (
        <ModalTipo
          open={!!tipoActivo}
          onOpenChange={(v) => !v && setTipoActivo(null)}
          onGenerado={cargar}
        />
      )}
    </motion.div>
  );
};

export default DocumentosPage;